import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import {
  filterErrorsWithoutResponseStatus,
  isNotUndefined,
} from "utils/common";
import { handleInvalidMemoSectionRequest } from "utils/sdk";

import toast from "components/Toast";

import SectionForm, {
  SectionValuesType,
} from "entities/MemoTemplate/components/SectionForm";
import {
  createMemoTemplateSection,
  IMemoTemplateSection,
  memoSectionAIPromptCreateAndAIPromptCreate,
  updateMemoTemplateSectionsOrder,
  useMemoTemplateDetails,
} from "entities/MemoTemplate/sdk";

const AddSectionForm = ({
  onCancel,
  onSectionCreated,
  afterMemoSectionId,
  formikRef,
}: {
  onCancel: () => void;
  onSectionCreated: () => void;
  afterMemoSectionId: number | null;
  formikRef?: React.Ref<any>;
}) => {
  const params = useParams<{ memoId: string }>();
  const memoId = Number(params.memoId);
  const { data: memo, mutate: mutateMemo } = useMemoTemplateDetails({
    memo_id: memoId,
  });

  const sections = useMemo(() => memo?.sections || [], [memo?.sections]);

  const handleMemoSectionValidRequest = useCallback(
    (section: IMemoTemplateSection) => {
      const secIds = sections.map((section) => section.memo_section_id);

      if (_.isNull(afterMemoSectionId)) {
        return updateMemoTemplateSectionsOrder({
          memoId,
          sectionsOrder: [section.memo_section_id, ...secIds],
        }).then(() => mutateMemo().then(() => onSectionCreated()));
      }

      const before = _.takeWhile(secIds, (id) => id !== afterMemoSectionId);
      const after = _.dropWhile(secIds, (id) => id !== afterMemoSectionId);
      const sectionsOrder = [
        ...before,
        _.first(after),
        section.memo_section_id,
        ..._.tail(after),
      ].filter(isNotUndefined);

      return updateMemoTemplateSectionsOrder({
        memoId,
        sectionsOrder,
      }).then(() => mutateMemo().then(() => onSectionCreated()));
    },
    [afterMemoSectionId, memoId, mutateMemo, onSectionCreated, sections]
  );

  const handleSubmit = (data: SectionValuesType): Promise<any> => {
    if (!_.isNil(data.ai_prompt)) {
      return memoSectionAIPromptCreateAndAIPromptCreate({
        memoId: memoId,
        data: _.pick(data, [
          "title",
          "section_type",
          "break_before",
          "ai_prompt",
        ]),
      })
        .then(handleMemoSectionValidRequest)
        .catch(handleInvalidMemoSectionRequest);
    }

    return createMemoTemplateSection({
      memoId,
      data: {
        ..._.pick(data, [
          "title",
          "section_type",
          "break_before",
          "due_diligence_section",
          "limit_image_section_height",
          "table_section_compact_view",
          "sources_and_uses_section_display_mode",
          "amenities_section_display_image",
        ]),
        checklist_table: data.checklist_table.map((row, rowIndex) =>
          row.map((value, colIndex) => ({
            value,
            is_editable: data.checklist_table_editable_cells.some(
              (item) => item.row === rowIndex && item.column === colIndex
            ),
          }))
        ),
      },
    })
      .then(handleMemoSectionValidRequest)
      .catch((errors) => {
        const filteredErrors = filterErrorsWithoutResponseStatus({
          errors,
        });
        _.forEach(filteredErrors, (e: string) => toast.errorMessage(e));
      });
  };

  return (
    <SectionForm
      initialValues={{}}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      formikRef={formikRef}
    />
  );
};

export default AddSectionForm;
